import * as Sentry from '@sentry/react';

/**
 * Set user context in Sentry for better error tracking
 * @param {Object} user - User object with id, email, and optional name
 */
export const setSentryUser = (user) => {
	if (!user) {
		Sentry.setUser(null);
		return;
	}

	Sentry.setUser({
		id: user.id,
		email: user.email,
		username: user.name || user.email,
	});
};

/**
 * Clear user context in Sentry (e.g., on logout)
 */
export const clearSentryUser = () => {
	Sentry.setUser(null);
};

/**
 * Capture a caught exception and send it to Sentry
 * Use this for errors that are caught but still need to be tracked
 *
 * @param {Error} error - The error object to capture
 * @param {Object} context - Optional additional context
 * @param {Object} context.tags - Tags to categorize the error
 * @param {Object} context.extra - Extra data to include with the error
 * @param {string} context.level - Severity level: 'fatal', 'error', 'warning', 'info', 'debug'
 *
 * @example
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureException(error, {
 *     tags: { section: 'payment', operation: 'checkout' },
 *     extra: { userId: user.id, amount: 100 },
 *     level: 'error'
 *   });
 * }
 */
export const captureException = (error, context = {}) => {
	const { tags = {}, extra = {}, level = 'error' } = context;

	Sentry.captureException(error, {
		level,
		tags,
		extra,
	});
};

/**
 * Capture a custom message/event in Sentry
 * Use this for non-error tracking (e.g., important warnings or info)
 *
 * @param {string} message - The message to capture
 * @param {Object} context - Optional additional context
 * @param {Object} context.tags - Tags to categorize the message
 * @param {Object} context.extra - Extra data to include
 * @param {string} context.level - Severity level: 'fatal', 'error', 'warning', 'info', 'debug'
 *
 * @example
 * captureMessage('Payment processing took longer than expected', {
 *   tags: { section: 'payment' },
 *   extra: { duration: 5000 },
 *   level: 'warning'
 * });
 */
export const captureMessage = (message, context = {}) => {
	const { tags = {}, extra = {}, level = 'info' } = context;

	Sentry.captureMessage(message, {
		level,
		tags,
		extra,
	});
};
